"use client";

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Button,
} from "@ac-expo/ui-components";
import CaretDownIcon from "../../../public/images/svg/SvgWebIcon/CaretDownIcon";
import filterStore from "../../services/FilterStore";
import { EventTypeType, getEventTypeTranslation } from "@ac-expo/api-types";
import { TrashIcon } from "@web-app/src/icons";
import { useEffect, useRef, useState } from "react";

export function EventTypeFilter() {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const selectedTypes = filterStore.selectedEventTypes;

  const handleTypeChange = (type: EventTypeType, checked: boolean) => {
    let updatedTypes = [...selectedTypes];
    if (checked) {
      updatedTypes.push(type);
    } else {
      updatedTypes = updatedTypes.filter((t) => t !== type);
    }
    filterStore.setSelectedEventTypes(updatedTypes);
  };

  const handleResetFilters = () => {
    filterStore.setSelectedEventTypes([]);
  };

  const getPlaceholder = () => {
    const firstTag =
      filterStore.selectedEventTypes.at(0) ??
      filterStore.selectedEventTypes.at(0);
    let label = "Types d'événements";

    switch (firstTag) {
      case "fair":
      case "sporting":
      case "festival":
      case "concert":
      case "cultural":
      case "other":
      case "professionalFair":
        label = getEventTypeTranslation(firstTag);
        break;
      default:
        break;
    }

    if (filterStore.selectedEventTypes.length > 1) {
      return `${label} +${filterStore.selectedEventTypes.length - 1}`;
    }
    return label;
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setOpen(false);
    }
  };

  const handleEscapePress = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      setOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscapePress);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapePress);
    };
  }, []);

  return (
    <DropdownMenu open={open}>
      <div className="flex w-full flex-row items-center gap-2">
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="h-[41px] w-full justify-between bg-white font-normal text-gray-500"
            onClick={() => setOpen((prev) => !prev)}
          >
            {getPlaceholder()}
            <CaretDownIcon height={20} width={20} fill={"#344054"} />
          </Button>
        </DropdownMenuTrigger>
        {filterStore.selectedEventTypes.length > 0 && (
          <Button
            onClick={handleResetFilters}
            variant="ghost"
            aria-label="Réinitialiser les filtres"
            className="self-center p-0 px-1"
          >
            <TrashIcon height={15} width={15} fill={"#344054"} />
          </Button>
        )}
      </div>
      <DropdownMenuContent className="w-56" ref={dropdownRef}>
        <DropdownMenuLabel>Types d&apos;événements</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuCheckboxItem
          checked={selectedTypes.includes("cultural")}
          onCheckedChange={(checked: boolean) =>
            handleTypeChange("cultural", checked)
          }
        >
          Culturel
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={selectedTypes.includes("sporting")}
          onCheckedChange={(checked: boolean) =>
            handleTypeChange("sporting", checked)
          }
        >
          Sportif
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={selectedTypes.includes("fair")}
          onCheckedChange={(checked: boolean) =>
            handleTypeChange("fair", checked)
          }
        >
          Foires & salons grand public
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={selectedTypes.includes("professionalFair")}
          onCheckedChange={(checked: boolean) =>
            handleTypeChange("professionalFair", checked)
          }
        >
          Salons professionnels
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={selectedTypes.includes("concert")}
          onCheckedChange={(checked: boolean) =>
            handleTypeChange("concert", checked)
          }
        >
          Spectacles & concerts
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={selectedTypes.includes("festival")}
          onCheckedChange={(checked: boolean) =>
            handleTypeChange("festival", checked)
          }
        >
          Festivals
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={selectedTypes.includes("other")}
          onCheckedChange={(checked: boolean) =>
            handleTypeChange("other", checked)
          }
        >
          Autres
        </DropdownMenuCheckboxItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default EventTypeFilter;
